import { CopyIcon, Refresh } from '@blueprinthq/joy'
import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Link,
  ModalBody,
  ModalContent,
  ModalFooter,
  Spacer,
  Switch,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react'
import { Bell, VideoCamera } from '@components/icons'
import { datadogLogs } from '@datadog/browser-logs'
import {
  EVENT_SETTING_TELEHEALTH,
  EventType,
  NotificationType
} from '@handlers/clinician-calendar/models'
import { PencilIcon } from '@handlers/sessions/components/document-viewer/components/Icon'
import { useExperienceManager, useTelehealthLinks } from '@hooks'
import { TrashIcon } from '@icons'
import { Subject } from '@material-ui/icons'
import moment from 'moment'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSessionControllerPostSession } from '~/clinician-api'
import { useCalendarEvent } from '../../context/calendar-event.context'
import { useCalendarModals } from './event-modals-manager'

interface ViewEventModalProps {
  onEdit: () => void
  onClose: () => void
  onClickDelete: () => void
}

export const ViewEventModal: React.FC<ViewEventModalProps> = ({
  onEdit,
  onClose,
  onClickDelete
}) => {
  const { currentEvent, getRecurringTitle, user } = useCalendarEvent()
  const toast = useToast()
  const { telehealthUrl } = useTelehealthLinks()
  const history = useHistory()

  const [isStartingSession, setIsStartingSession] = useState(false)
  const { mutateAsync: createSessionDraft } = useSessionControllerPostSession()
  const { isPlusPlanEnabled } = useExperienceManager()

  if (!currentEvent) {
    return null
  }

  const copyTelehealthUrl = () => {
    navigator.clipboard.writeText(telehealthUrl)
    toast({
      title: 'Link copied to clipboard',
      status: 'success',
      duration: 3000,
      isClosable: true
    })
  }

  const copyDetailsToClipboard = () => {
    navigator.clipboard.writeText(`Hi ${currentEvent?.patient?.firstName} ${
      currentEvent?.patient?.lastName
    } - your ${currentEvent.getDuration()}
-minute ${currentEvent.setting} session is confirmed for ${moment(
      currentEvent.start
    ).format('ddd MMM D')} at ${moment(currentEvent.start).format(
      'h:mm A'
    )}. Questions?
Email ${user?.email}`)
    toast({
      title: 'Details copied to clipboard',
      status: 'success',
      duration: 3000,
      isClosable: true
    })
  }

  const buildSessionUrl = (
    patientId: string,
    sessionId: string,
    isTelehealth: boolean
  ): string => {
    if (isTelehealth) {
      return `/patient/${patientId}/telehealth/session/${sessionId}`
    }
    return `/patient/${patientId}/session/${sessionId}`
  }

  const handleStartSession = async () => {
    const patientId = currentEvent?.patient?.id

    if (!patientId) {
      // Fallback to select-client page if no patient ID
      history.push('/start-session/select-client')
      return
    }

    setIsStartingSession(true)

    try {
      const sessionDraft = await createSessionDraft({
        data: {
          patientId,
          isExtension: null,
          extensionInitiatedFrom: null,
          isChime: false,
          isDictation: false,
          isDailyco: currentEvent.setting === EVENT_SETTING_TELEHEALTH,
          resumeInProgress: true,
          medplumAppointmentId: currentEvent.id,
          isAssistEnabled: isPlusPlanEnabled
        }
      })

      if (!sessionDraft?.id) {
        throw new Error('Session draft creation returned no ID')
      }

      const sessionUrl = buildSessionUrl(
        patientId,
        sessionDraft.id,
        currentEvent.setting === EVENT_SETTING_TELEHEALTH
      )

      history.push(sessionUrl)

      // Add small delay to ensure window opens before modal closes
      setTimeout(() => onClose(), 100)
    } catch (error: any) {
      datadogLogs.logger.error(
        'Failed to create session',
        {
          patientId
        },
        error
      )

      toast({
        description:
          error?.response?.data?.message ||
          'Please try again or select a client manually.',
        status: 'error',
        duration: 5000,
        isClosable: true
      })

      // Fallback to select-client page
      history.push('/start-session/select-client')
    } finally {
      setIsStartingSession(false)
    }
  }

  return (
    <ModalContent>
      <ModalBody>
        <VStack spacing="xsmall" align="stretch">
          <Flex justify="space-between" align="center">
            {currentEvent.eventType === EventType.CLIENT_SESSION ? (
              <Link
                href={`/patient/${currentEvent.patient?.id}`}
                transition="opacity 0.2s"
                _focus={{ boxShadow: 'none' }}
              >
                <Avatar
                  name={`${currentEvent?.patient?.firstName} ${currentEvent?.patient?.lastName}`}
                  size="sm"
                  h="40px"
                  w="40px"
                  bg="#EAEEFD"
                  fontWeight="bold"
                  color="primary"
                  cursor="pointer"
                />
              </Link>
            ) : (
              <Text fontSize="18px">{currentEvent.subject}</Text>
            )}
            {!currentEvent.sourceCalendarType && (
              <HStack>
                <IconButton
                  p="xsmall"
                  borderRadius="8px"
                  variant="outline"
                  aria-label="Edit Event"
                  onClick={onEdit}
                >
                  <PencilIcon />
                </IconButton>
                <IconButton
                  p="xsmall"
                  borderRadius="8px"
                  variant="outline"
                  aria-label="Cancel Event"
                  onClick={onClickDelete}
                >
                  <TrashIcon />
                </IconButton>
              </HStack>
            )}
          </Flex>
          {currentEvent.eventType === EventType.CLIENT_SESSION && (
            <Link
              href={`/patient/${currentEvent.patient?.id}`}
              fontSize="18px"
              color="primary"
              textDecoration="none"
              _hover={{
                textDecoration: 'underline',
                '&::after': {
                  content: '"→"',
                  marginLeft: '4px',
                  display: 'inline-block'
                }
              }}
            >
              {`${currentEvent?.patient?.firstName} ${currentEvent?.patient?.lastName}`}
            </Link>
          )}
          {/* Event Details Card */}
          <VStack spacing="0px" gap="4px" align="flex-start">
            {currentEvent.eventType === EventType.CLIENT_SESSION && (
              <Text fontSize="sm">{currentEvent.serviceType?.text}</Text>
            )}
            <HStack spacing="xsmall">
              <Text fontSize="sm">
                {moment(currentEvent.start).format('h:mm A')} -{' '}
                {moment(currentEvent.end).format('h:mm A')}
              </Text>
              <Text fontSize="sm" color="gray">
                {moment(currentEvent.start).format('dddd MMM D, YYYY')}
              </Text>
            </HStack>
            <Text fontSize="sm">{getRecurringTitle(currentEvent)}</Text>
          </VStack>
          <VStack
            spacing="0px"
            gap="16px"
            pb="16px"
            pt="8px"
            align="flex-start"
          >
            <Divider />
            {currentEvent.eventType === EventType.OTHER_EVENT ? (
              <Text fontSize="sm">{currentEvent.notes}</Text>
            ) : (
              <>
                <HStack gap="16px" width="100%">
                  <Box>
                    <VideoCamera width="24px" fill="#A1A1A1" />
                  </Box>
                  <Text fontSize="sm">{telehealthUrl}</Text>
                  <Spacer />
                  <Box>
                    <IconButton
                      aria-label="copy"
                      onClick={copyTelehealthUrl}
                      variant="outline"
                      style={{
                        borderRadius: '4px'
                      }}
                      icon={<CopyIcon size="sm" />}
                    />
                  </Box>
                </HStack>
                <HStack gap="16px" width="100%" align="flex-start">
                  <Box>
                    <Subject width="24px" fill="#A1A1A1" />
                  </Box>
                  <Text fontSize="sm">
                    Hi {currentEvent?.patient?.firstName}{' '}
                    {currentEvent?.patient?.lastName} - your{' '}
                    {currentEvent.getDuration()}-minute {currentEvent.setting}{' '}
                    session is confirmed for{' '}
                    {moment(currentEvent.start).format('ddd MMM D')} at{' '}
                    {moment(currentEvent.start).format('h:mm A')}. Questions?
                    Email {user?.email}
                  </Text>
                  <Spacer />
                  <IconButton
                    aria-label="copy"
                    onClick={copyDetailsToClipboard}
                    variant="outline"
                    style={{
                      borderRadius: '4px'
                    }}
                    icon={<CopyIcon size="sm" />}
                  />
                </HStack>
              </>
            )}
          </VStack>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button w="100%" borderRadius="8px" onClick={onClose} variant="outline">
          Close
        </Button>
        {currentEvent.eventType === EventType.CLIENT_SESSION && (
          <Button
            w="100%"
            borderRadius="8px"
            onClick={handleStartSession}
            isLoading={isStartingSession}
            loadingText="Starting Session..."
          >
            Start Session
          </Button>
        )}
      </ModalFooter>
    </ModalContent>
  )
}
